import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ProductCard, ProductCardSkeleton } from "@/components/storefront/ProductCard";
import { CategoryIcon } from "@/components/storefront/CategoryIcon";
import { listCategoryTopProducts, listCategoryTopBrands } from "@/lib/products.functions";
import { BrandLogo } from "@/components/storefront/BrandLogo";
import { categorySlug } from "@/lib/category-icon";
import { useIsMobile } from "@/hooks/use-mobile";

export function HomeCategorySection({
  psId,
  name,
  descendantIds,
  limit,
}: {
  psId: number;
  name: string;
  descendantIds: number[];
  limit?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  // Στα κινητά κρατάμε λιγότερες κάρτες ανά ενότητα ώστε το DOM να μένει μικρό
  // (λιγότεροι υπολογισμοί στιλ/διάταξης). Οι ενότητες φορτώνουν μόνο όταν
  // μπουν στο viewport, οπότε το πλήθος είναι ήδη γνωστό πριν αποδοθούν.
  const isMobile = useIsMobile();
  const cardLimit = limit ?? (isMobile ? 6 : 8);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const { data } = useQuery({
    queryKey: ["home", "cat", psId, descendantIds.length, cardLimit],
    queryFn: () =>
      listCategoryTopProducts({ data: { categoryPsIds: descendantIds, limit: cardLimit } }),
    enabled: visible,
    staleTime: 5 * 60_000,
  });

  const products = data?.products ?? [];
  // Σταδιακή απόδοση των καρτών (ανά frame) ώστε να μη γίνεται ένα μεγάλο
  // commit του React — σπάει τις «μεγάλες εργασίες» του κύριου νήματος.
  const [shown, setShown] = useState(4);
  useEffect(() => {
    if (shown >= products.length) return;
    const raf = requestAnimationFrame(() => setShown((n) => n + 4));
    return () => cancelAnimationFrame(raf);
  }, [shown, products.length]);

  // Top-3 μάρκες της κατηγορίας (μόνο αφού γίνει visible το section).
  const { data: brandsData } = useQuery({
    queryKey: ["home", "cat-brands", psId, descendantIds.length],
    queryFn: () => listCategoryTopBrands({ data: { categoryPsIds: descendantIds, limit: 5 } }),
    enabled: visible,
    staleTime: 30 * 60_000,
  });
  const topBrands = brandsData?.brands ?? [];

  // Αφού φορτώσει και δεν υπάρχουν προϊόντα, μην εμφανίζεις την ενότητα.
  if (visible && data && products.length === 0) return null;

  // Πριν μπει στο viewport, δεν φτιάχνουμε το βαρύ grid με τα 12 skeleton
  // cards — απλά κρατάμε χώρο με ένα ελαφρύ placeholder ώστε να μειωθεί το
  // αρχικό Style & Layout (το grid χτίζεται μόλις γίνει visible).
  if (!visible) {
    return (
      <section
        ref={ref}
        className="mx-auto max-w-none px-4 py-8"
        aria-hidden
      >
        <div className="mb-4 h-7 w-48 rounded bg-muted/60" />
        <div className="min-h-[260px] sm:min-h-[300px] lg:min-h-[360px]" />
      </section>
    );
  }

  return (
    <section ref={ref} className="mx-auto max-w-none px-4 py-8">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="flex items-center gap-2 text-lg font-bold">
            <CategoryIcon psId={psId} className="h-5 w-5 text-primary" />
            {name}
          </h2>
          {topBrands.length > 0 && (
            <div className="hidden items-center gap-2 sm:flex">
              <span aria-hidden className="text-sm">⭐</span>
              {topBrands.map((b, i) => (
                <Link
                  key={b.ps_id}
                  to="/brand/$id"
                  params={{ id: b.slug }}
                  title={`${b.name} — ${b.productCount} προϊόντα`}
                  aria-label={`Δες τα προϊόντα ${b.name}`}
                  className={`animate-float-y inline-flex h-9 w-[76px] shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-primary/40 bg-white px-2 py-1 transition-transform hover:scale-105 hover:border-primary ${i % 2 === 0 ? "-translate-y-1 rotate-[-3deg]" : "translate-y-1 rotate-[3deg]"}`}
                  style={{ animationDelay: `${i * 220}ms`, animationDuration: `${3 + (i % 3) * 0.6}s` }}
                >
                  {b.logo_url ? (
                    <BrandLogo
                      logoUrl={b.logo_url}
                      name={b.name}
                      width={96}
                      className="h-full w-full rounded-none border-0 bg-white"
                      imgClassName="max-h-[22px] max-w-[62px] object-contain object-center"
                    />
                  ) : (
                    <span className="line-clamp-2 text-center text-[9px] font-extrabold leading-tight text-foreground">
                      {b.name}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
        <Link
          to="/c/$slug"
          params={{ slug: categorySlug(psId, name) }}
          aria-label={`Δες τα όλα — ${name}`}
          className="text-sm text-link hover:underline"
        >
          Δες τα όλα →
        </Link>
      </div>
      {products.length === 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: cardLimit }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {products.slice(0, Math.min(cardLimit, shown)).map((p) => (
            <ProductCard key={p.id} product={p as never} />
          ))}
        </div>
      )}
    </section>
  );
}